import { Component, Inject, Input, OnDestroy, OnInit, signal, WritableSignal, ChangeDetectorRef } from '@angular/core';
import { MatSort, Sort } from '@angular/material/sort';
import { GalaxyFilterChipInjectionToken, GalaxyFilterInterface } from '@vendasta/galaxy/filter/chips';
import { GalaxyDataSource, Row } from '@vendasta/galaxy/table';
import { GalaxyColumnDef } from '@vendasta/galaxy/table/src/table.interface';
import { BehaviorSubject, from, Observable, of, Subject, Subscription } from 'rxjs';
import { CategoryRow, GroupAndServiceService } from './events-group-and-service.service';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Calendar, Group, MeetingType, Service } from '@vendasta/meetings';
import { GroupInterface, ServiceInterface } from '@vendasta/meetings/lib/_internal/interfaces';
import { DeleteBookingLinkConfirmationDialogComponent } from '../delete-booking-link-confirmation-dialog/delete-booking-link-confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { PageEvent } from '@angular/material/paginator';
import { map, startWith } from 'rxjs/operators';
import {
  CALENDAR_PAGE_LINK_TOKEN,
  EVENT_TYPE_PAGE_TOKEN,
  FEATURE_MEETING_SESSION_EVENT_TOKEN,
  GROUP_PAGE_TOKEN,
  SERVICE_PAGE_TOKEN,
} from '../../data-providers/providers';
import { MeetingSchedulerStoreService } from '../../data-providers/meeting-scheduler-store.service';
import { TranslateService } from '@ngx-translate/core';
import {
  GRID_VIEW,
  LIST_VIEW,
  MEETING_SCHEDULER_CONTEXT_INJECTION_TOKEN$,
  MS_CONTEXT,
  POSTHOG_ACTIONS,
  POSTHOG_CATEGORIES,
  POSTHOG_KEYS,
} from '../../constants';

import { SalesTeamDialogComponent } from './sales-team-dialog/sales-team-dialog.component';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { first } from 'rxjs/operators';

@Component({
  selector: 'meeting-scheduler-group-and-service',
  templateUrl: './events-group-and-service.component.html',
  styleUrls: ['./events-group-and-service.component.scss'],
  providers: [GroupAndServiceService, { provide: GalaxyFilterChipInjectionToken, useExisting: GroupAndServiceService }],
  standalone: false,
})
export class GroupAndServiceComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  @Input() selectedToggle: string;
  groupPageLink: string;
  name: string;
  memberCount: string;
  emptyStateTitle: string;
  emptyStateSubtitle: string;
  calendarPageLink: string;
  servicePageLink: string;
  eventTypePageLink: string;
  dataSource: GalaxyDataSource<Row, void, MatSort> = {} as GalaxyDataSource<Row, void, MatSort>;
  calendarLoadError$: Observable<boolean>;
  columns$?: Observable<GalaxyColumnDef[]>;
  isMobileScreen$: Observable<boolean>;
  pageSizeOptions: number[];
  isLoading$ = new BehaviorSubject<boolean>(true);
  pageSize: number;
  data: CategoryRow[] = [];
  noDataIcon = 'https://storage.googleapis.com/meeting-scheduler-prod-public-images/Icon-for-no-team.svg';
  personalCalendar$: Observable<Calendar>;
  sessionCalendar$: Observable<Calendar>;
  groupCalendars$$: Subject<Calendar[]> = new Subject<Calendar[]>();
  selectedGroupCalendarId$$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  protected MSContextType = MS_CONTEXT;

  // Track pin states to override any data source issues
  private pinStateOverrides = new Map<string, boolean>();

  constructor(
    private readonly categoryService: GroupAndServiceService,
    @Inject(CALENDAR_PAGE_LINK_TOKEN) public readonly calendarPageLink$: Observable<string>,
    @Inject(GROUP_PAGE_TOKEN) public readonly groupPageLink$: Observable<string>,
    @Inject(SERVICE_PAGE_TOKEN) public readonly servicePageLink$: Observable<string>,
    @Inject(EVENT_TYPE_PAGE_TOKEN) public readonly eventTypePageLink$: Observable<string>,
    @Inject(FEATURE_MEETING_SESSION_EVENT_TOKEN)
    readonly featureSessionEventEnabled$: Observable<boolean>,
    @Inject(MEETING_SCHEDULER_CONTEXT_INJECTION_TOKEN$) protected context: MS_CONTEXT,
    private readonly meetingSchedulerStoreService: MeetingSchedulerStoreService,
    private readonly alertService: SnackbarService,
    private readonly dialog: MatDialog,
    private router: Router,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly translate: TranslateService,
    private readonly analyticsService: ProductAnalyticsService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.subscriptions.push(
      calendarPageLink$.subscribe((value) => (this.calendarPageLink = value)),
      groupPageLink$.subscribe((value) => (this.groupPageLink = value)),
      servicePageLink$.subscribe((value) => (this.servicePageLink = value)),
      eventTypePageLink$.subscribe((value) => (this.eventTypePageLink = value)),
    );
    this.isMobileScreen$ = this.breakpointObserver.observe([Breakpoints.XSmall]).pipe(map((result) => result.matches));
  }
  displayOption: WritableSignal<typeof LIST_VIEW | typeof GRID_VIEW> = signal(LIST_VIEW);

  ngOnInit(): void {
    this.displayOption.set(this.meetingSchedulerStoreService.displayOption());
    this.pageSizeOptions = this.meetingSchedulerStoreService.pageSizeOptions;
    this.pageSize = this.meetingSchedulerStoreService.pageSize;
    this.meetingSchedulerStoreService.tableViewEnabled = true;
    this.meetingSchedulerStoreService.selectedToggle = this.selectedToggle;
    this.getTranslations(this.selectedToggle);
    this.loadData(this.selectedToggle);
    this.personalCalendar$ = this.meetingSchedulerStoreService.loadPersonalCalendar();
    this.sessionCalendar$ = this.meetingSchedulerStoreService.loadSessionCalendar();
    const groupCalendars$ = this.meetingSchedulerStoreService.loadGroupCalendars();
    this.subscriptions.push(
      groupCalendars$.subscribe((calendars) => {
        this.groupCalendars$$.next(calendars);
      }),
    );
    this.calendarLoadError$ = this.meetingSchedulerStoreService.getCalendarLoadErrors().pipe(
      map((errorSet) => errorSet.size > 0),
      startWith(false),
    );
  }

  navigateToGroups(groupPageLink: string): void {
    const url = `${groupPageLink}`;
    this.router.navigateByUrl(url);
  }

  handleEventTableCreate(eventTypePageLink: string, calendarId: string): void {
    const calendarType = this.getCalendarType(calendarId);
    this.router.navigate([eventTypePageLink, calendarId], {
      queryParams: { calendar_type: calendarType },
    });
  }

  private getCalendarType(calendarId: string): string {
    // Check if it's a personal calendar
    try {
      const personalCalendar = this.getPersonalCalendarSync();
      if (personalCalendar && personalCalendar.id === calendarId) {
        return 'personal';
      }

      // Check if it's a session calendar
      const sessionCalendar = this.getSessionCalendarSync();
      if (sessionCalendar && sessionCalendar.id === calendarId) {
        return 'session';
      }

      // Check if it's a group calendar
      const groupCalendars = this.getGroupCalendarsSync();
      if (groupCalendars && groupCalendars.some((calendar) => calendar.id === calendarId)) {
        return 'team';
      }
    } catch (error) {
      console.warn('Error determining calendar type, defaulting to personal:', error);
    }

    // Default to personal if unable to determine
    return 'personal';
  }

  private getPersonalCalendarSync(): Calendar | null {
    try {
      let personalCalendar: Calendar | null = null;
      const subscription = this.personalCalendar$.pipe(first()).subscribe({
        next: (calendar) => {
          personalCalendar = calendar;
        },
        error: () => {
          personalCalendar = null;
        },
      });
      subscription.unsubscribe();
      return personalCalendar;
    } catch {
      return null;
    }
  }

  private getSessionCalendarSync(): Calendar | null {
    try {
      let sessionCalendar: Calendar | null = null;
      const subscription = this.sessionCalendar$.pipe(first()).subscribe({
        next: (calendar) => {
          sessionCalendar = calendar;
        },
        error: () => {
          sessionCalendar = null;
        },
      });
      subscription.unsubscribe();
      return sessionCalendar;
    } catch {
      return null;
    }
  }

  private getGroupCalendarsSync(): Calendar[] | null {
    try {
      let groupCalendars: Calendar[] | null = null;
      const subscription = this.groupCalendars$$.pipe(first()).subscribe({
        next: (calendars) => {
          groupCalendars = calendars;
        },
        error: () => {
          groupCalendars = null;
        },
      });
      subscription.unsubscribe();
      return groupCalendars;
    } catch {
      return null;
    }
  }

  getFilterHeader(selectedToggle?: string): string {
    return selectedToggle
      ? this.translate.instant(`MEETING_SCHEDULER.MEETING_LIST.${selectedToggle.toUpperCase()}`)
      : '';
  }

  getTranslations(selectedToggle?: string): void {
    if (!selectedToggle) return;

    const prefix = `MEETING_SCHEDULER.MEETING_LIST.TABLE.VIEW.${selectedToggle.toUpperCase()}.`;
    [this.name, this.memberCount, this.emptyStateTitle, this.emptyStateSubtitle] = [
      'NAME',
      'MEMBER_COUNT',
      'EMPTY_STATE_TITLE',
      'EMPTY_STATE_SUBTITLE',
    ].map((key) => `${prefix}${key}`);
  }

  loadData(selectedToggle) {
    this.columns$ = this.categoryService.getColumn$(selectedToggle).pipe(map((cols) => [...cols]));
    this.dataSource = this.categoryService.getDataSource();
    this.dataSource.loading$ = of(true);
    this.categoryService.getDataFromApi(selectedToggle, this.dataSource);
    this.subscriptions.push(
      this.dataSource.state$.subscribe((val) => {
        if (val.initialLoadCompleted) {
          this.data = this.categoryService.ROW_DATA;
        }
      }),
    );
  }

  onFilterChanged(filters: GalaxyFilterInterface[]): void {
    this.categoryService.updateFilers(filters);
  }

  sortData(sort: Sort): void {
    this.dataSource?.setSorting([
      {
        active: sort.active,
        direction: sort.direction,
      } as MatSort,
    ]);
  }

  handleBookingClick(link: string) {
    this.analyticsService.trackEvent(POSTHOG_KEYS.VIEW_EVENT_LINK, POSTHOG_CATEGORIES.USER, POSTHOG_ACTIONS.CLICK);
    window.open(link, '_blank', 'noopener,noreferrer');
  }

  copyToClipboard(link: string): void {
    navigator.clipboard
      .writeText(link)
      .then(() => {
        this.analyticsService.trackEvent(POSTHOG_KEYS.VIEW_EVENT_LINK, POSTHOG_CATEGORIES.USER, POSTHOG_ACTIONS.CLICK);
        this.alertService.openSuccessSnack('MEETING_SCHEDULER.MEETINGS_SHARED.ALERTS.LINK_COPIED');
      })
      .catch((err) => {
        console.error(err);
      });
  }
  openSalesTeamDialog(eventTypePageLink: string, groupCalendars: Calendar[]): void {
    if (this.context === MS_CONTEXT.MS_CONTEXT_SMB) {
      const groupCalendar = groupCalendars[0];
      this.handleEventTableCreate(eventTypePageLink, groupCalendar.id);
      return;
    }

    const dialogRef = this.dialog.open(SalesTeamDialogComponent, {
      width: '400px',
    });

    const dialogSub = dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.handleEventTableCreate(result.eventTypePageLink, result.calendarId);
      }
    });

    this.subscriptions.push(dialogSub);
  }

  onMenuClosed() {
    (document.activeElement as HTMLElement)?.blur();
  }

  onMenuItemHover(event: MouseEvent) {
    (event.target as HTMLElement).blur();
  }

  // Method to get the actual pin state, checking overrides first
  getPinState(row: CategoryRow): boolean {
    const entityId = row.mid || row.id;
    if (this.pinStateOverrides.has(entityId)) {
      return this.pinStateOverrides.get(entityId)!;
    }
    return row.isPinned;
  }

  // Getter that returns a Map of pin states for all rows
  get pinStatesMap(): Map<string, boolean> {
    const statesMap = new Map<string, boolean>();

    this.data.forEach((row) => {
      const entityId = row.mid || row.id;
      let pinState: boolean;

      if (this.pinStateOverrides.has(entityId)) {
        pinState = this.pinStateOverrides.get(entityId)!;
      } else {
        pinState = row.isPinned;
      }

      statesMap.set(entityId, pinState);
    });

    return statesMap;
  }

  // Method to sort data based on pin states
  private sortDataByPinState() {
    // Sort the data array in place
    this.data.sort((a, b) => {
      const aPinned = this.getPinState(a);
      const bPinned = this.getPinState(b);
      return Number(bPinned) - Number(aPinned);
    });

    // Update service data with sorted data
    this.categoryService.ROW_DATA = [...this.data];

    // Force the dataSource to refetch by setting shouldRefetch to true
    if (this.dataSource && this.dataSource.state) {
      this.dataSource.state = {
        ...this.dataSource.state,
        shouldRefetch: true,
      };
    }

    // Force change detection
    this.cdr.detectChanges();
  }

  async togglePin(row: CategoryRow) {
    if (row.isDefaultGroup) {
      this.alertService.openWithOptions('MEETING_SCHEDULER.MEETING_LIST.TABLE.VIEW.PIN.UPDATE.RESTRICTED.ERROR');
      return;
    }

    // Determine the new pin status using our override method
    const currentPinStatus = this.getPinState(row);
    const newPinStatus = !currentPinStatus;
    const entityId = row.mid || row.id;

    // Set the override state immediately for UI update
    this.pinStateOverrides.set(entityId, newPinStatus);
    this.cdr.detectChanges();

    try {
      if (this.isService(entityId)) {
        await this.updateServiceData(row, newPinStatus);
      } else if (this.isGroup(entityId)) {
        await this.updateGroupData(row, newPinStatus);
      } else {
        await this.updateMeetingData(row, newPinStatus);
      }

      // After successful API call, update the actual data and sort
      this.sortDataByPinState();
    } catch (error) {
      // Revert the override if API failed
      this.pinStateOverrides.set(entityId, currentPinStatus);
      this.cdr.detectChanges();
      this.alertService.openWithOptions('MEETING_SCHEDULER.MEETING_LIST.PIN_UPDATE_FAILED');
    }
  }

  updateDataSource(isDeleteAction: boolean, mid: string, isPinned?: boolean) {
    let data: CategoryRow[] = [...this.categoryService.ROW_DATA];

    if (isDeleteAction) {
      data = data.filter((row) => row.mid !== mid && row.id !== mid);
    } else {
      data = data.map((row) => {
        // Check both row.mid and row.id to find the correct row
        if (row.mid === mid || row.id === mid) {
          return {
            ...row,
            isPinned: isPinned ?? row.isPinned,
          };
        }
        return row;
      });
    }

    // Sort data with pinned items first using our getPinState method
    const sortedData = data.sort((a, b) => {
      const aPinned = this.getPinState(a);
      const bPinned = this.getPinState(b);
      return Number(bPinned) - Number(aPinned);
    });

    // Update the service data FIRST - this is what getDataSource() uses
    this.categoryService.ROW_DATA = sortedData;
    this.data = [...sortedData];

    // Now recreate the dataSource from the updated service data
    this.dataSource = this.categoryService.getDataSource();
    this.dataSource.loading$ = of(false);

    // Trigger change detection
    this.cdr.detectChanges();
  }

  async updateMeetingData(row: CategoryRow, newPinStatus: boolean) {
    // Extract minutes from "15 mins" format and convert to "15m" format that getDurationValue expects
    const durationInMinutes = parseInt(row.duration);
    const duration = !isNaN(durationInMinutes) ? `${durationInMinutes}m` : undefined;

    const updatedMeetingType = {
      id: row.mid,
      calendarId: row.calendarId,
      isPinned: newPinStatus,
      duration,
      meetingTypeSlug: row.meetingTypeSlug,
    } as MeetingType;

    try {
      const currentDataSource = this.dataSource;
      await this.updateMeetingType(row.mid, updatedMeetingType, ['isPinned', 'duration', 'meetingTypeSlug']);
      this.dataSource = currentDataSource;
    } catch (err) {
      console.error('Error in updating meeting type:', err);
      throw err; // Re-throw to allow caller to handle
    }
  }

  async updateMeetingType(id: string, meetingType: MeetingType, updateFields?: (keyof MeetingType)[]): Promise<void> {
    try {
      await this.meetingSchedulerStoreService.updateMeetingType(id, '', meetingType, updateFields);
    } catch (err) {
      console.error(err);
      // Don't show error snackbar here since the caller (togglePin) handles it centrally
      throw err; // Re-throw to allow caller to handle
    }
  }

  async updateGroupData(row: CategoryRow, newPinStatus: boolean) {
    const mt = {
      id: row.mid,
      isPinned: newPinStatus,
    } as Group;
    try {
      await this.updateGroup(row.mid, mt, ['isPinned']);
    } catch (err) {
      console.error('Error in updating group:', err);
      throw err; // Re-throw to allow caller to handle
    }
  }

  async updateGroup(id: string, groupData: Group, updateFields?: (keyof GroupInterface)[]): Promise<void> {
    try {
      await this.meetingSchedulerStoreService.updateGroup(id, groupData, updateFields);
    } catch (err) {
      console.error(err);
      // Don't show error snackbar here since the caller (togglePin) handles it centrally
      throw err; // Re-throw to allow caller to handle
    }
  }

  async updateServiceData(row: CategoryRow, newPinStatus: boolean) {
    const mt = {
      id: row.mid,
      isPinned: newPinStatus,
    } as Service;
    try {
      await this.updateService(row.mid, mt, ['isPinned']);
    } catch (err) {
      console.error('Error in updating service:', err);
      throw err; // Re-throw to allow caller to handle
    }
  }

  async updateService(id: string, data: Service, updateFields?: (keyof ServiceInterface)[]): Promise<void> {
    try {
      await this.meetingSchedulerStoreService.updateService(id, data, updateFields);
    } catch (err) {
      console.error(err);
      // Don't show error snackbar here since the caller (togglePin) handles it centrally
      throw err; // Re-throw to allow caller to handle
    }
  }

  isGroup(id: string) {
    return id.startsWith('GRP-');
  }

  isService(id: string) {
    return id.startsWith('SVS-');
  }

  handleDeleteClicked(id: string): void {
    this.dialog.open(DeleteBookingLinkConfirmationDialogComponent, {
      maxWidth: '500px',
      data: {
        category: this.selectedToggle,
        entityID: id,
        validateAssociation: !this.isService(id),
        confirmationCallback: () => from(this.deletedata(id)),
      },
    });
  }

  async deletedata(id) {
    if (this.isService(id)) {
      this.deleteService(id);
    } else if (this.isGroup(id)) {
      this.deleteGroup(id);
    } else {
      this.deleteMeetingType(id);
    }
    this.updateDataSource(true, id);
  }

  async deleteMeetingType(id: string): Promise<void> {
    const currentDataSource = this.dataSource;
    try {
      await this.meetingSchedulerStoreService.deleteMeetingType(id);
      this.dataSource = currentDataSource;
      this.analyticsService.trackEvent(POSTHOG_KEYS.EVENT_TYPE_DELETED, POSTHOG_CATEGORIES.USER, POSTHOG_ACTIONS.CLICK);
      this.alertService.openSuccessSnack('MEETING_SCHEDULER.MEETING_LIST.ALERTS.DELETE_BOOKING_LINK_SUCCESS');
    } catch (err) {
      this.alertService.openErrorSnack('MEETING_SCHEDULER.MEETING_LIST.ALERTS.DELETE_BOOKING_LINK_ERROR');
      console.error(err);
    }
  }

  async deleteGroup(id: string): Promise<void> {
    try {
      await this.meetingSchedulerStoreService.deleteGroup(id);
      this.analyticsService.trackEvent(POSTHOG_KEYS.GROUP_DELETED, POSTHOG_CATEGORIES.USER, POSTHOG_ACTIONS.CLICK);
      this.alertService.openSuccessSnack('MEETING_SCHEDULER.MEETING_LIST.ALERTS.DELETE_GROUP_SUCCESS');
    } catch (err) {
      this.alertService.openErrorSnack('MEETING_SCHEDULER.MEETING_LIST.ALERTS.DELETE_GROUP_ERROR');
      console.error(err);
    }
  }

  async deleteService(id: string): Promise<void> {
    try {
      await this.meetingSchedulerStoreService.deleteService(id);
      this.analyticsService.trackEvent(POSTHOG_KEYS.SERVICE_DELETED, POSTHOG_CATEGORIES.USER, POSTHOG_ACTIONS.CLICK);
      this.alertService.openSuccessSnack('MEETING_SCHEDULER.MEETING_LIST.ALERTS.DELETE_SERVICE_MENU_SUCCESS');
    } catch (err) {
      this.alertService.openErrorSnack('MEETING_SCHEDULER.MEETING_LIST.ALERTS.DELETE_SERVICE_MENU_ERROR');
      console.error(err);
    }
  }

  navigateToEditURL(mid: string): void {
    this.router.navigate([`${this.calendarPageLink}/${mid}`]);
  }

  handleView(row): void {
    if (this.isService(row.mid)) {
      const url = `${this.servicePageLink}/${row.mid}`;
      this.router.navigateByUrl(url);
    } else if (this.isGroup(row.mid)) {
      const url = `${this.groupPageLink}/${row.mid}`;
      this.router.navigateByUrl(url);
    } else {
      const eventTypeId = row.mid;
      const calendarType = this.getCalendarType(row.calendarId);
      this.router.navigate([this.eventTypePageLink, row.calendarId, eventTypeId], {
        queryParams: { calendar_type: calendarType },
      });
    }
  }

  handleScheduleClick(row): void {
    const eventTypeId = row.mid;
    const calendarType = this.getCalendarType(row.calendarId);
    this.router.navigate([this.eventTypePageLink, row.calendarId, eventTypeId], {
      queryParams: {
        calendar_type: calendarType,
        event_type: 'Session',
        show_schedule_accordion: 'true',
      },
    });
  }

  displayChanges(event) {
    this.meetingSchedulerStoreService.displayOption.set(event === GRID_VIEW ? GRID_VIEW : LIST_VIEW);
    if (event === LIST_VIEW) {
      this.pageSizeOptions = [5, 10, 15];
      this.pageSize = 10;
    } else {
      this.pageSizeOptions = [6, 9, 15];
      this.pageSize = 9;
    }
    this.meetingSchedulerStoreService.pageSizeOptions = this.pageSizeOptions;
    this.meetingSchedulerStoreService.pageSize = this.pageSize;
    const pageChanged: PageEvent = {
      pageIndex: 0,
      pageSize: this.pageSize,
      length: this.categoryService.ROW_DATA.length,
    };
    this.dataSource.pageChanged(pageChanged);
    this.displayOption.set(event);
  }

  getTooltip(name: string, limit: number): string | null {
    return name?.length > limit ? name : null;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];
  }
}
